import { createApi } from "@reduxjs/toolkit/query/react";
import { baseQueryWithReauth } from "../api.config";

export const showingFeedbackApi = createApi({
  reducerPath: "showingFeedbackApi",
  baseQuery: baseQueryWithReauth,
  tagTypes: ["ShowingFeedback", "ShowingStats"],
  endpoints: (builder) => ({
    // Get all listings
    getAllListings: builder.query({
      query: (params = {}) => {
        const queryParams = new URLSearchParams();
        if (params.status) queryParams.append("status", params.status);
        if (params.search) queryParams.append("search", params.search);
        if (params.sortBy) queryParams.append("sortBy", params.sortBy);
        if (params.sortOrder) queryParams.append("sortOrder", params.sortOrder);
        if (params.limit) queryParams.append("limit", params.limit);
        return `/showing-feedback?${queryParams.toString()}`;
      },
      providesTags: (result) =>
        result?.data
          ? [
              ...result.data.map(({ _id }) => ({ type: "ShowingFeedback", id: _id })),
              { type: "ShowingFeedback", id: "LIST" },
            ]
          : [{ type: "ShowingFeedback", id: "LIST" }],
    }),

    // Get single listing
    getListingById: builder.query({
      query: (id) => `/showing-feedback/${id}`,
      providesTags: (result, error, id) => [{ type: "ShowingFeedback", id }],
    }),

    // Create listing
    createListing: builder.mutation({
      query: (listingData) => ({
        url: "/showing-feedback",
        method: "POST",
        body: listingData,
      }),
      invalidatesTags: [
        { type: "ShowingFeedback", id: "LIST" },
        { type: "ShowingStats" },
      ],
    }),

    // Update listing
    updateListing: builder.mutation({
      query: ({ id, ...listingData }) => ({
        url: `/showing-feedback/${id}`,
        method: "PUT",
        body: listingData,
      }),
      invalidatesTags: (result, error, { id }) => [
        { type: "ShowingFeedback", id },
        { type: "ShowingFeedback", id: "LIST" },
      ],
    }),

    // Delete listing
    deleteListing: builder.mutation({
      query: (id) => ({
        url: `/showing-feedback/${id}`,
        method: "DELETE",
      }),
      invalidatesTags: [
        { type: "ShowingFeedback", id: "LIST" },
        { type: "ShowingStats" },
      ],
    }),

    // Add feedback
    addFeedback: builder.mutation({
      query: ({ listingId, ...feedbackData }) => ({
        url: `/showing-feedback/${listingId}/feedback`,
        method: "POST",
        body: feedbackData,
      }),
      invalidatesTags: (result, error, { listingId }) => [
        { type: "ShowingFeedback", id: listingId },
        { type: "ShowingFeedback", id: "LIST" },
        { type: "ShowingStats" },
      ],
    }),

    // Update feedback
    updateFeedback: builder.mutation({
      query: ({ listingId, feedbackId, ...feedbackData }) => ({
        url: `/showing-feedback/${listingId}/feedback/${feedbackId}`,
        method: "PUT",
        body: feedbackData,
      }),
      invalidatesTags: (result, error, { listingId }) => [
        { type: "ShowingFeedback", id: listingId },
        { type: "ShowingStats" },
      ],
    }),

    // Delete feedback
    deleteFeedback: builder.mutation({
      query: ({ listingId, feedbackId }) => ({
        url: `/showing-feedback/${listingId}/feedback/${feedbackId}`,
        method: "DELETE",
      }),
      invalidatesTags: (result, error, { listingId }) => [
        { type: "ShowingFeedback", id: listingId },
        { type: "ShowingStats" },
      ],
    }),

    // Get stats
    getShowingStats: builder.query({
      query: () => "/showing-feedback/stats",
      providesTags: [{ type: "ShowingStats" }],
    }),

    // Toggle feedback form
    toggleFeedbackForm: builder.mutation({
      query: ({ id, enabled }) => ({
        url: `/showing-feedback/${id}/toggle-form`,
        method: "POST",
        body: { enabled },
      }),
      invalidatesTags: (result, error, { id }) => [
        { type: "ShowingFeedback", id },
      ],
    }),

    // Regenerate form token
    regenerateFormToken: builder.mutation({
      query: (id) => ({
        url: `/showing-feedback/${id}/regenerate-token`,
        method: "POST",
      }),
      invalidatesTags: (result, error, id) => [
        { type: "ShowingFeedback", id },
      ],
    }),

    // Get price analysis
    getPriceAnalysis: builder.query({
      query: (id) => `/showing-feedback/${id}/price-analysis`,
    }),

    // Public: Get listing by token
    getListingByToken: builder.query({
      query: (token) => `/showing-feedback/public/${token}`,
    }),

    // Public: Submit feedback by token
    submitFeedbackByToken: builder.mutation({
      query: ({ token, ...feedbackData }) => ({
        url: `/showing-feedback/public/${token}/feedback`,
        method: "POST",
        body: feedbackData,
      }),
    }),
  }),
});

export const {
  useGetAllListingsQuery,
  useGetListingByIdQuery,
  useCreateListingMutation,
  useUpdateListingMutation,
  useDeleteListingMutation,
  useAddFeedbackMutation,
  useUpdateFeedbackMutation,
  useDeleteFeedbackMutation,
  useGetShowingStatsQuery,
  useToggleFeedbackFormMutation,
  useRegenerateFormTokenMutation,
  useGetPriceAnalysisQuery,
  useGetListingByTokenQuery,
  useSubmitFeedbackByTokenMutation,
} = showingFeedbackApi;
